import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useBlog } from "../context/BlogContext";
import { BLOG_IMAGE_ALT_MAP } from "../services/blogImageAltMap";

export default function BlogSection({
  title = "Blog",
  subtitle = "",
  initialCount = 6,
  step = 6,
  mode = "grid",
  relatedCount = 3,
  excludeName = "",
}) {
  const { posts, loading, error } = useBlog();
  const [visible, setVisible] = useState(initialCount);



  const list = useMemo(() => {
    let arr = Array.isArray(posts) ? [...posts] : [];
    if (excludeName) arr = arr.filter((p) => p?.name !== excludeName);

    arr.sort((a, b) => (b?.date || "").localeCompare(a?.date || ""));

    if (mode === "related") return arr.slice(0, relatedCount);
    return arr.slice(0, visible);
  }, [posts, visible, mode, relatedCount, excludeName]);

  if (loading) {
    return (
      <section className="bg-white py-16 px-4 sm:px-6 md:px-24">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center gap-3">
            <div className="w-5 h-5 rounded-full border-2 border-[#00B3FF] border-t-transparent animate-spin" />
            <p className="text-[#5f6f8f] font-medium">Loading blogs...</p>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="h-full rounded-2xl overflow-hidden shadow-md border border-[#e7eeff] bg-white animate-pulse"
              >
                <div className="w-full h-[210px] bg-[#eef4ff]" />
                <div className="px-7 py-8">
                  <div className="h-3 w-24 bg-gray-200 rounded mx-auto" />
                  <div className="mt-5 h-6 w-4/5 bg-gray-200 rounded mx-auto" />
                  <div className="mt-6 space-y-2">
                    <div className="h-3 w-full bg-gray-200 rounded" />
                    <div className="h-3 w-11/12 bg-gray-200 rounded" />
                    <div className="h-3 w-10/12 bg-gray-200 rounded" />
                  </div>
                  <div className="mt-8 h-10 w-44 bg-gray-200 rounded-full mx-auto" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="bg-white py-16 px-4 sm:px-6 md:px-24">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-3xl sm:text-5xl font-bold text-[#49628b] text-center">
          {title}
        </h2>

        {subtitle ? (
          <p className="mt-4 text-center text-[#5f6f8f] max-w-3xl mx-auto">
            {subtitle}
          </p>
        ) : null}

        {error ? (
          <div className="mt-8 p-4 rounded-xl bg-red-50 border border-red-200 text-red-700">
            {error}
          </div>
        ) : null}

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
          {list.map((p) => (
            <article
              key={p?.id || p?.name}
              className="h-full rounded-2xl overflow-hidden shadow-md border border-[#e7eeff] flex flex-col bg-white"
            >
              <div className="bg-white">
                {p?.image ? (
                  <img
                    src={p.image}
                    srcSet={p.imageSrcSet || undefined}
                    sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, calc(100vw - 2rem)"
                    alt={BLOG_IMAGE_ALT_MAP[p.image] || p?.title || "Blog article image"}
                    className="w-full h-[210px] object-contain bg-white"
                    loading="lazy"
                  />
                ) : (
                  <div className="w-full h-[210px] bg-[#eef4ff]" />
                )}
              </div>

              <div className="bg-[#4b638f] text-white px-7 py-8 flex-1 flex flex-col">
                <div className="text-xs opacity-90 mb-4 flex items-center justify-center gap-1">
                  <span className="inline-block w-2 h-2 rounded-full bg-white/50" />
                  <span>{(p?.date || "").split("T")[0]}</span>
                </div>

                <h3 className="text-xl font-bold text-center leading-snug">
                  {p?.title || ""}
                </h3>

                <p className="mt-6 text-sm text-center leading-relaxed opacity-95 line-clamp-3">
                  {p?.excerpt || ""}
                </p>

                <div className="mt-auto pt-8 flex justify-center">
                  <Link
                    to={`/blog/${p?.name}`}
                    className="bg-[#00B3FF] hover:bg-[#00A0E5] text-[#0a2540] font-semibold px-10 py-3 rounded-full shadow-md transition"
                    aria-label={`Read more about ${p?.title || "this article"}`}
                  >
                    Read More
                    <span className="sr-only">
                      {" "}
                      about {p?.title || "this article"}
                    </span>
                  </Link>
                </div>
              </div>
            </article>
          ))}
        </div>

        {mode === "grid" && posts.length > visible ? (
          <div className="mt-14 flex justify-center">
            <button
              onClick={() => setVisible((v) => v + step)}
              className="bg-[#00B3FF] hover:bg-[#00A0E5] text-[#0a2540] font-semibold px-10 py-3 rounded-md shadow-lg transition"
            >
              Load More
            </button>
          </div>
        ) : null}
      </div>
    </section>
  );
}
